<template>
  <div class="develop-summary-page" ref="developSummaryPage">
    <section v-if="!!profile" class="develop-summary-background" style="padding-top: 25px">
      <div class="container maxWidth-sm text-big">
        <div class="row">
          <div class="col-sm-8 col-sm-offset-2 text-center" style="margin-top: 20px">
            <p>You are developing</p>
            <h1 data-test="develop-page-title" class="current-strength-heading h-underline-info">
              {{ currentProgress.strengthItemDisplayTitle }}
            </h1>
          </div>
          <div class="col-sm-2 col-xs-pull-4 col-sm-pull-0 text-right">
            <button
              data-test="change-strength-button"
              class="btn btn-default btn-md"
              style="margin: 10px 0"
              @click="$router.push('/resources/playlists/the-34-strengths')"
            >
              <span class="glyphicon glyphicon-transfer"></span> Change Strength
            </button>
          </div>
          <div
            v-if="hasCompletedFirstSelfReview"
            class="col-sm-6 col-sm-offset-3"
            style="margin-top: 20px"
          >
            <div class="panel tile-shadow panel-default text-center">
              <div class="panel-body">
                <h3 style="margin-bottom: 20px">My Reviews</h3>
                <develop-progress :model="profile.developProgress"></develop-progress>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <section style="padding: 0px"></section>
    <section style="padding-top: 20px">
      <div class="container maxWidth-sm text-big">
        <div class="row">
          <div v-for="(week, index) in currentPlanWeeks" :key="week.id" class="col-sm-12">
            <develop-plan-week
              :model="week"
              :expanded="weeksExpanded[index]"
              @expand="handleWeekClick(index, true)"
              @contract="handleWeekClick(index, false)"
            />
          </div>
        </div>
      </div>
    </section>
    <section style="padding: 0px"></section>
    <section>
      <resource-themes-list
        v-if="!!beYourOwnBestCoachPlaylist"
        :doc="beYourOwnBestCoachPlaylist"
        tile-css-class="tile-shadow"
        css-theme=""
      >
        <template v-slot:title>
          <h2 class="h-underline-default" style="position: relative; top: 4px">
            Be Your Own Best Coach
          </h2>
        </template>
      </resource-themes-list>
      <div class="container maxWidth-md text-big">
        <div class="row">
          <div v-if="!!beYourOwnBestCoachPlaylist" class="col-sm-12" style="margin-top: 20px"></div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { debug as d } from "debug";
import axios from "axios";
import scrollIntoViewIfNeededPony from "scroll-into-view-if-needed";
import { computed, getCurrentInstance, onBeforeMount, ref } from "vue";
import DevelopProgress from "../components/develop-progress.vue";
import { CmsResourcePlaylist } from "../services/cms/cms-type-definitions";
import cms from "../services/cms/cms";
import { DevelopStrengthProgressApiModel, ProfileApiModel } from "../api-models";
import ResourceThemesList from "../components/cms/resource-themes-list.vue";
import DevelopPlanWeek from "../components/develop/develop-plan-week.vue";
import cmsContentMap from "../services/cms/cms-content-map";
import { DevelopPlan } from "../services/cms/develop-plan";
import { getPlanWeeks, PlanWeekModel } from "../components/develop/develop-plan-models";

const debug = d("cs.DevelopSummaryPage");

const profile = ref<ProfileApiModel | null>(null);
const currentPlan = ref<DevelopPlan | null>(null);
const selectedWeekIndex = ref<number | null>(null);
const beYourOwnBestCoachPlaylist = ref<CmsResourcePlaylist | null>(null);

const developSummaryPage = ref<HTMLElement | null>(null);

const currentProgress = computed(() => {
  return profile.value?.developProgress?.currentStrengthProgress;
});

const hasCompletedFirstSelfReview = computed(() => {
  return !!currentProgress.value && typeof currentProgress.value.week1Score === "number";
});

const currentPlanWeeks = computed(() => {
  return !!currentPlan.value && !!currentProgress.value
    ? getPlanWeeks(cms, currentPlan.value, currentProgress.value)
    : null;
});

const weeksExpanded = computed(() => {
  return currentPlanWeeks.value
    ? currentPlanWeeks.value.map((w, i) => i === selectedWeekIndex.value)
    : null;
});

function handleWeekClick(weekIndex: number, shouldExpand: boolean): void {
  debug("handleWeekClick", weekIndex, shouldExpand);
  selectedWeekIndex.value = shouldExpand ? weekIndex : null;
}

function scrollToCurrentDay(): void {
  debug("scrollToCurrentDay");
  const instance = getCurrentInstance()?.proxy as any;

  // get element with class .develop-step.current
  const day = document.querySelector(".develop-step.current");

  // const day = instance?.$el.querySelector(".develop-step.current");
  if (!day) {
    debug("Can't scroll: No current day found");
    return;
  }

  const pageContentElement = day.closest("body");
  if (!pageContentElement) {
    debug("Can't scroll: No page content element found");
    return;
  }
  debug("Scrolling into view if needed");
  scrollIntoViewIfNeededPony(day, {});
}

onBeforeMount(async () => {
  debug("beforeMount");
  const profileResponse = await axios.get("/api/profile");
  if (!profileResponse.data.developProgress) {
    window.location.href = "/session";
    return;
  }
  profile.value = profileResponse.data;

  if (profile.value?.developProgress?.currentStrengthProgress) {
    const plans = await cms.getDevelopPlans();
    const planMap: { [strengthItemId: string]: DevelopPlan } = plans.reduce((obj, plan) => {
      obj[plan.strengthItemId] = plan;
      return obj;
    }, {});
    currentPlan.value =
      planMap[profile.value.developProgress.currentStrengthProgress.strengthItemId];
  } else {
    currentPlan.value = null;
  }

  beYourOwnBestCoachPlaylist.value = await cms.getByUid(
    "cms-resource-playlist",
    cmsContentMap.develop.beYourOwnBestCoachPlaylistUid,
  );
  setTimeout(() => {
    if (currentPlanWeeks.value) {
      selectedWeekIndex.value = currentPlanWeeks.value.findIndex((w) =>
        w.steps.some((s) => s.isCurrent),
      );

      setTimeout(() => {
        scrollToCurrentDay();
      }, 500);
    }
  }, 100);
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="less">
// .develop-summary-page .cs-carousel-container {
//   width: 100% !important;
// }
</style>

<style scoped lang="less">
// .develop-summary-page {
// }

.develop-week-panel {
  background-color: #f8f8f8;
}

.current-strength-heading {
  margin-top: 0;
}

.develop-summary-background {
  background-color: #f8f8f8;
}

.progress-column {
  background-color: #eee;
}
</style>
